<template>
  <div class="imgs">
    <div ref="imgsRef" class="gallery">
      <div v-for="(x, i) in list.filter(Boolean)" :key="i" class="img">
        <img :key="i" :src="x" loading="lazy" />
      </div>
    </div>
    <div
      :class="['control-bar', { show: !ctx?.videoMeta.paused }]"
      :style="styleBar"
      @pointerdown="onMouseDown"
    >
      <div ref="lp" class="touchable pointer pointer-left">
        <div class="progress-line before" :data-time="displayStartTime">
          <div class="inner-line"></div>
        </div>
      </div>
      <div ref="mp" class="touchable content" :style="styleContent">
        <div class="play-pointer" :style="stylePlayCursor">
          <div
            class="progress-line bottom"
            :data-time="displayCurrentTime"
          ></div>
        </div>
      </div>
      <div ref="rp" class="touchable pointer pointer-right">
        <div class="progress-line after" :data-time="displayEndTime">
          <div class="inner-line"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { IVideoControlContext } from './types'
import { useTime } from '~/hooks/useTime'

const { formatDuration } = useTime()

const ctx = inject<IVideoControlContext>('dynamic-cover-video-control')

const props = defineProps<{ list: any[]; maxDuration: number }>()

const emit = defineEmits(['change'])

const lp = ref()
const rp = ref()
const mp = ref()

const imgsRef = ref<HTMLElement>()

const imgsWidth = computed(() =>
  imgsRef.value?.clientWidth ? imgsRef.value?.clientWidth : 776
)

const maxPercent = computed(() => props.maxDuration / ctx!.videoMeta.duration)

const maxWidth = computed(
  () => (maxPercent.value > 1 ? 1 : maxPercent.value) * imgsWidth.value
)

const offset = ref(0)
const width = ref(maxWidth.value)

function formatDurationTime(s: number) {
  return formatDuration(Math.ceil(s) * 1000)
}

const currentTime = computed(() => ctx!.videoMeta.currentTime)
const displayCurrentTime = computed(() => formatDurationTime(currentTime.value))

const startTime = computed(
  () => (offset.value / imgsWidth.value) * (ctx?.videoMeta.duration || 0)
)
const displayStartTime = computed(() => formatDurationTime(startTime.value))

const endTime = computed(
  () =>
    ((offset.value + width.value) / imgsWidth.value) *
    (ctx?.videoMeta.duration || 0)
)
const displayEndTime = computed(() => formatDurationTime(endTime.value))

const styleBar = computed(() => ({
  transform: `translateX(${offset.value}px)`,
}))

const styleContent = computed(() => ({
  width: `${width.value}px`,
}))

const stylePlayCursor = computed(() => ({
  display: lockDom.value ? 'none' : 'block',
  transform: currentTime.value
    ? `translateX(${
        (ctx!.videoMeta.currentTime / ctx!.videoMeta.duration -
          offset.value / imgsWidth.value) *
        imgsWidth.value
      }px)`
    : 'none',
}))

let startX = 0

const lockDom = ref<Element | null>()
let memOffset: number, memWidth: number

const onMouseDown = (e: MouseEvent) => {
  if (!ctx?.videoMeta.paused) {
    ctx?.togglePlay()
  }

  const target = (e.target as HTMLElement)!.closest('.touchable')

  if (lockDom.value) {
    return
  }

  if (target === lp.value) {
    lockDom.value = lp.value
  }
  if (target === rp.value) {
    lockDom.value = rp.value
  }
  if (target === mp.value) {
    lockDom.value = mp.value
  }

  startX = e.clientX
  memOffset = offset.value
  memWidth = width.value
  document.body.style.cursor = 'col-resize'
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

const onMouseMove = (e: MouseEvent) => {
  e.stopPropagation()
  e.preventDefault()
  if (!startX) {
    return
  }

  const delta = e.clientX - startX

  let nextOffset = offset.value
  let nextWidth = width.value

  if (lockDom.value === mp.value) {
    nextOffset = memOffset + delta
  }
  if (lockDom.value === rp.value) {
    nextWidth = memWidth + delta
  }
  if (lockDom.value === lp.value) {
    nextOffset = memOffset + delta
    nextWidth = memWidth - delta
    if (nextOffset > memWidth + memOffset) {
      return
    }
    if (nextWidth > maxWidth.value) {
      return
    }
  }

  if (nextOffset + nextWidth > imgsWidth.value) {
    return
  }
  if (nextOffset < 0) {
    return
  }

  // 最大时长限制
  if (nextWidth > maxWidth.value) {
    nextWidth = maxWidth.value
  }
  if (nextWidth < 0) {
    nextWidth = 0
  }

  offset.value = nextOffset
  width.value = nextWidth
}

const onMouseUp = () => {
  memOffset = 0
  memWidth = 0
  lockDom.value = null
  startX = 0
  document.body.style.cursor = ''
  document.removeEventListener('pointermove', onMouseMove)
  document.removeEventListener('pointerup', onMouseUp)
}

watch(
  () => [startTime.value, endTime.value],
  (paris) => {
    emit('change', paris)
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})
</script>

<style lang="scss" scoped>
.gallery {
  pointer-events: none;
}

.progress-line {
  position: relative;
  display: none;

  &::before,
  &::after {
    box-shadow: 0px 2px 20px 0px rgba(81, 80, 96, 0.4);
  }

  &.before::before {
    right: -2px;
    transform: translateY(calc(-100% - 5px));
  }
  &.after::before {
    left: -2px;
    transform: translateY(calc(-100% - 5px));
  }
  &::before {
    content: attr(data-time);
    position: absolute;
    text-align: center;
    background-color: var(--primary-color);
    padding: 4px 4px;
    line-height: 1;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    transform: translateY(calc(-100% - 6px));
    border-radius: 4px;
  }
  &::after {
    content: '';
    position: absolute;
    height: 0;
    width: 0;
    left: 50%;
    transform: translate(-50%, -6px);
    border-right: 4px solid transparent;
    border-left: 4px solid transparent;
    border-top: 4px solid var(--primary-color);
  }
  &.bottom {
    &::before,
    &::after {
      top: 75px;
    }

    &::before {
      top: 48px;
      left: 50%;
      transform: translate(-50%);
    }
    &::after {
      top: 51px;
      border-right: 4px solid transparent;
      border-left: 4px solid transparent;
      border-bottom: 4px solid var(--primary-color);
      border-top: 0;
    }
  }
}

.control-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -5px;
  display: flex;

  &.show {
    .progress-line.bottom {
      display: block;
    }
  }
  &:hover {
    .progress-line {
      display: block;
    }
  }

  .play-pointer {
    transition: transform 250ms linear;
    @apply absolute top-0 left-[0] w-[3px] h-full z-[1] rounded-[1px];
    background-color: var(--primary-color);
    box-shadow: 0 0 2px 2px rgba(0, 0, 0, 0.1);
  }

  .content {
    position: relative;
    flex: 1;
    cursor: grab;
    border-top: 2px solid var(--primary-color);
    border-bottom: 2px solid var(--primary-color);
    pointer-events: all;
    background-color: rgba(0, 0, 0, 0.2);
  }

  .pointer {
    position: relative;
    width: 6px;
    background-color: var(--primary-color);
    pointer-events: all;
    cursor: ew-resize;
    box-sizing: border-box;

    &:hover .tip {
      color: #fff;
    }

    .inner-line {
      position: absolute;
      height: 28px;
      top: 8px;
      left: 2px;
      width: 2px;
      background-color: #fff;
      border-radius: 2px;
    }

    &-left {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }
    &-right {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 4px;
      border-top-right-radius: 4px;
    }
  }
}

.imgs {
  @apply w-full h-[44px] relative bg-back-4 flex p-[2px];
  .gallery {
    width: 100%;
  }
  .img {
    @apply h-full w-[7.1428%];
    display: inline-block;
  }
  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
    transition: all 0.3s ease;
    pointer-events: none;
    user-select: none;
    &:hover {
      transform: scale(1.1);
      box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
